import { useCallbackOne as useCallback } from 'use-memo-one';
import gql from 'graphql-tag';
import { useMutation as useApolloMutation, type MutationHookOptions } from '@apollo/react-hooks';
import { commitMutation, graphql, useMutation } from 'react-relay';
import { type ContextId, type ExtensionId } from '@atlassian/forge-ui-types';
import {
	type GQLInvokeExtensionEntrypoint,
	type GQLInvokeExtensionInput,
	type GQLInvokeExtensionPayload,
	type GQLInvokeExtensionResponse,
} from '../graphql/types';
import { createAtlAttributionHeader } from '../../utils';
import type { DocumentNode } from 'graphql';
import { type useInvokeExtensionRelayMutation } from './__generated__/useInvokeExtensionRelayMutation.graphql';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';

export interface MutationVariables {
	input: GQLInvokeExtensionInput;
}

export interface MutationData {
	invokeExtension: GQLInvokeExtensionResponse;
}

export type InvokeExtensionInvoker = (
	payload: GQLInvokeExtensionPayload,
	entryPoint?: GQLInvokeExtensionEntrypoint,
) => Promise<{ data?: MutationData }>;

export const invokeExtensionMutation: DocumentNode = gql`
	mutation forge_ui_invokeExtension($input: InvokeExtensionInput!) {
		invokeExtension(input: $input) {
			success
			response {
				body
			}
			contextToken {
				jwt
				expiresAt
			}
			errors {
				message
				extensions {
					errorType
					statusCode
					... on InvokeExtensionPayloadErrorExtension {
						fields {
							authInfoUrl
						}
					}
				}
			}
		}
	}
`;

const useInvokeExtensionRelay: (...args: any[]) => any = (
	contextIds: ContextId[],
	extensionId: ExtensionId,
	_options?: MutationHookOptions<MutationData, MutationVariables>,
) => {
	const customCommitFn = useCallback(
		(environment: Parameters<typeof commitMutation>[0], config: any) =>
			commitMutation<useInvokeExtensionRelayMutation>(environment, {
				...config,
				cacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
						},
					},
				},
			}),
		[contextIds],
	);

	const [commit] = useMutation<useInvokeExtensionRelayMutation>(
		graphql`
			mutation useInvokeExtensionRelayMutation($input: InvokeExtensionInput!) {
				invokeExtension(input: $input) {
					success
					response {
						body
					}
					contextToken {
						jwt
						expiresAt
					}
					errors {
						message
						extensions {
							errorType
							statusCode
							... on InvokeExtensionPayloadErrorExtension {
								fields {
									authInfoUrl
								}
							}
						}
					}
				}
			}
		`,
		customCommitFn,
	);

	return useCallback(
		(payload: GQLInvokeExtensionPayload, entryPoint: GQLInvokeExtensionEntrypoint = 'resolver') =>
			new Promise<{ data?: MutationData }>((resolve, reject) => {
				commit({
					variables: {
						input: {
							contextIds,
							extensionId,
							payload,
							entryPoint,
						},
					},
					onCompleted: (data: unknown) => resolve({ data: data as MutationData }),
					onError: reject,
				});
			}),
		[contextIds, extensionId, commit],
	);
};
export const useInvokeExtensionApollo: (...args: any[]) => any = (
	contextIds: ContextId[],
	extensionId: ExtensionId,
	options?: MutationHookOptions<MutationData, MutationVariables>,
) => {
	const [apolloMutationFn] = useApolloMutation<MutationData, MutationVariables>(
		invokeExtensionMutation,
		options,
	);

	return useCallback(
		(payload: GQLInvokeExtensionPayload, entryPoint: GQLInvokeExtensionEntrypoint = 'resolver') => {
			return apolloMutationFn({
				variables: {
					input: {
						contextIds,
						extensionId,
						payload,
						entryPoint,
					},
				},
				context: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
					},
				},
			});
		},
		[contextIds, extensionId, apolloMutationFn],
	);
};

export const useInvokeExtension = conditionalHooksFactory(
	() => fg('forge-ui-add-relay-invoke-extension-query'),
	useInvokeExtensionRelay,
	useInvokeExtensionApollo,
);
